import { makeStyles } from 'tss-react/mui';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import ListItem from '@mui/material/ListItem';

import { CatalogSearchResultListItem } from '@backstage/plugin-catalog';
import { FAQSearchResultItem } from '@internal/plugin-oneadvanced';
import {
  catalogApiRef,
  CATALOG_FILTER_EXISTS,
} from '@backstage/plugin-catalog-react';

import { SearchType } from '@backstage/plugin-search';
import {
  SearchBar,
  SearchFilter,
  SearchResult,
  SearchPagination,
  useSearch,
} from '@backstage/plugin-search-react';
import {
  Content,
  Header,
  Page,
} from '@backstage/core-components';
import HomeIcon from '@mui/icons-material/Home';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import WorkflowsIcon from '@mui/icons-material/AddCircleOutline';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { useApi } from '@backstage/core-plugin-api';

const useStyles = makeStyles()((theme) => ({
  bar: {
    padding: theme.spacing(1, 0),
  },
  filters: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  filter: {
    '& + &': {
      marginTop: theme.spacing(2.5),
    },
  },
}));

const SearchPage = () => {
  const { classes } = useStyles();
  const { types } = useSearch();
  const catalogApi = useApi(catalogApiRef);

  return (
    <Page themeId="home">
      <Header title="Search" />
      <Content>
        <Grid container direction="row">
          <Grid item xs={12}>
            <Paper className={classes.bar}>
              <SearchBar />
            </Paper>
          </Grid>
          <Grid item xs={3}>
            <SearchType.Accordion
              name="Result Type"
              defaultValue=""
              types={[
                {
                  value: 'software-catalog',
                  name: 'Catalog',
                  icon: <HomeIcon />,
                },
                {
                  value: 'techdocs',
                  name: 'Docs',
                  icon: <LibraryBooksIcon />,
                },
                {
                  value: 'workflows',
                  name: 'Workflows',
                  icon: <WorkflowsIcon />,
                },
                {
                  value: 'faq',
                  name: 'FAQs',
                  icon: <QuestionAnswerIcon />,
                },
              ]}
            />
            <Paper className={classes.filters}>
              {types.includes('techdocs') && (
                <SearchFilter.Select
                  className={classes.filter}
                  label="Entity"
                  name="name"
                  values={async () => {
                    // Return a list of entities which are documented.
                    const { items } = await catalogApi.getEntities({
                      fields: ['metadata.name'],
                      filter: {
                        'metadata.annotations.backstage.io/techdocs-ref':
                          CATALOG_FILTER_EXISTS,
                      },
                    });

                    const names = items.map(entity => entity.metadata.name);
                    names.sort();
                    return names;
                  }}
                />
              )}
              {types.includes('software-catalog') && (
                <>
                  <SearchFilter.Select
                    className={classes.filter}
                    label="Kind"
                    name="kind"
                    values={['API', 'Component', 'Domain', 'Group', 'System', 'User']}
                  />
                  <SearchFilter.Checkbox
                    className={classes.filter}
                    label="Lifecycle"
                    name="lifecycle"
                    values={['experimental', 'production']}
                  />
                </>
              )}
            </Paper>
          </Grid>
          <Grid item xs={9}>
            <SearchPagination />
            <SearchResult>
              {({ results }) => (
                <>
                  {results.map(({ type, document, highlight, rank }) => {
                    if (type === 'faq') {
                      return (
                        <ListItem key={document.location} divider alignItems="flex-start">
                          <FAQSearchResultItem
                            result={document as any}
                            rank={rank}
                          />
                        </ListItem>
                      );
                    }
                    if (type === 'workflows') {
                      return (
                        <CatalogSearchResultListItem
                          key={document.location}
                          icon={<WorkflowsIcon />}
                          result={document}
                          highlight={highlight}
                          rank={rank}
                        />
                      );
                    }
                    return (
                      <CatalogSearchResultListItem
                        key={document.location}
                        icon={type === 'techdocs' ? <LibraryBooksIcon /> : <HomeIcon />}
                        result={document}
                        highlight={highlight}
                        rank={rank}
                      />
                    );
                  })}
                </>
              )}
            </SearchResult>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};

export const searchPage = <SearchPage />;
